import React from 'react';
import theme from './PriceDisplay.module.scss';
import { useAppSelector } from '@/utils/hooks';
import { ProjectCurrencyDisplay } from '../../currency';
import { CurrencySymbol } from '@/components/localization';
import { PriceCalculationStep, PriceStepType, ProjectFeeSet } from '@/types';
import { Tooltip } from '@/components/Tooltip';
import { FeePreview, PriceCalculationDisplay } from '@/seller/components/fees';

export interface PriceDisplayProps {
    basePrice: number | null;
    finalPrice: number | null;
    projectFeeSet?: ProjectFeeSet | null;
}

const emptyFees: PriceCalculationStep[] = [];

export const PriceDisplay: React.FC<PriceDisplayProps> = ({ finalPrice, basePrice, projectFeeSet }) => {
    const project = useAppSelector((s) => s.global.activeProject!);

    if (basePrice === null || finalPrice === null) return null;

    const priceContent = (
        <span className={theme.container}>
            <div>
                <span className={theme.currency}>
                    <CurrencySymbol currency={project.currencyCode} />
                </span>
                <ProjectCurrencyDisplay value={finalPrice} hideParts={['currency']} />
            </div>
            <small>
                (<ProjectCurrencyDisplay value={basePrice || 0} />)
            </small>
        </span>
    );

    if (!projectFeeSet) {
        return priceContent;
    }

    return (
        <FeePreview configuration={projectFeeSet.fees} quantity={1} basePrice={basePrice}>
            {(calculation, loading) => {
                const hasFeeOrTaxSteps = calculation?.steps.some((step) =>
                    [PriceStepType.Fee, PriceStepType.Tax].includes(step.type)
                );

                if (!hasFeeOrTaxSteps) {
                    return priceContent;
                }

                return (
                    <Tooltip
                        color="light"
                        dynamicWidth
                        content={<PriceCalculationDisplay value={calculation?.steps ?? emptyFees} loading={loading} />}>
                        {priceContent}
                    </Tooltip>
                );
            }}
        </FeePreview>
    );
};
